import './inviteLinksWidget.scss'
import { useEffect, useMemo, useState } from 'react'
import {
    AnalyticsSelectors,
    // InviteLinksChart,
    InviteLinksSitesTable,
} from '@/features/analytics'
import { useInviteLinksStore } from '@/features/analytics/model'
import { useInviteLinksTimeSelectors } from '@/features/analytics/invite-links/hooks'
import { Link } from 'react-router'
import { useCreativesPrefetchQuery, useKktuPrefetchQuery } from '@/shared/api'
import { Title } from '@/shared/ui'
import { useGroupNameSites } from '../hooks/useGroupName'
import { CreateSiteInviteLink } from '@/features/analytics/components/create-invite-link/ui/CreateSiteInviteLink'
import { SiteLinkWithAnalyticsType } from '@/shared/api/site-links-group/site-links-group.types'
import {
    workspacePath,
    AdvertiserAccount,
    Site,
    useActiveWorkspace,
} from '@/true-fsd/data/advertiser-account'
import { isMasterLink } from '@/features/analytics/invite-links/ui/InviteLinksTable/shared/masterLink'
import { useSiteLinksByGroup } from '@/true-fsd/data/link'

export type InviteLinksSitesWidgetProps = {
    groupId: number
    activeSite: Site
    activeAccount: AdvertiserAccount | null
}
export const InviteLinksSitesWidget = ({
    groupId,
    activeSite,
    activeAccount,
}: InviteLinksSitesWidgetProps) => {
    useKktuPrefetchQuery()
    useCreativesPrefetchQuery({
        workspace_type: 'site',
        workspace_id: activeSite.site_id,
    })

    const groupName = useGroupNameSites({ siteId: activeSite.site_id })

    const { data: inviteLinks = [], isLoading: isTableLoading } =
        useSiteLinksByGroup({
            site_id: activeSite.site_id,
            site_links_group_id: groupId,
            filter: groupName !== 'Все ссылки',
        })

    const [optimisticInviteLinks, setOptimisticInviteLinks] = useState<
        SiteLinkWithAnalyticsType[]
    >([])

    const onRemoveInviteLink = (newLink: SiteLinkWithAnalyticsType) => {
        const newLinkCreatedAt = new Date(newLink.created_at)
        setOptimisticInviteLinks((prev) =>
            prev.filter(
                (link) =>
                    !(
                        link.site_link_id === 0 &&
                        link.site_link_name === newLink.site_link_name &&
                        link.budget === newLink.budget &&
                        link.created_at.getHours() ===
                            newLinkCreatedAt.getHours() &&
                        link.created_at.getDate() ===
                            newLinkCreatedAt.getDate() &&
                        link.created_at.getFullYear() ===
                            newLinkCreatedAt.getFullYear() &&
                        link.created_at.getMonth() ===
                            newLinkCreatedAt.getMonth()
                    )
            )
        )
    }

    const onInviteLinkUpdated = (updatedLink: SiteLinkWithAnalyticsType) => {
        setOptimisticInviteLinks((prev) => {
            const updatedLinkCreatedAt = new Date(updatedLink.created_at)
            return prev.map((link) =>
                link.site_link_id === 0 &&
                link.site_link_name === updatedLink.site_link_name &&
                link.budget === updatedLink.budget &&
                link.created_at.getHours() ===
                    updatedLinkCreatedAt.getHours() &&
                link.created_at.getDate() === updatedLinkCreatedAt.getDate() &&
                link.created_at.getFullYear() ===
                    updatedLinkCreatedAt.getFullYear() &&
                link.created_at.getMonth() === updatedLinkCreatedAt.getMonth()
                    ? updatedLink // Replace the temporary row with the finalized row
                    : link
            )
        })
    }

    const combinedInviteLinks = useMemo(() => {
        // Use a Map to deduplicate rows based on their IDs
        const linkMap: Record<number, SiteLinkWithAnalyticsType> = {}
        // Add backend-fetched invite links
        ;(inviteLinks || []).forEach((link) => {
            linkMap[link.site_link_id] = link
        })
        // Add optimistic invite links, overriding any duplicates
        optimisticInviteLinks.forEach((link) => {
            linkMap[link.site_link_id] = link
        })
        return Object.values(linkMap)
    }, [inviteLinks, optimisticInviteLinks])

    const {
        startDateInvite,
        endDateInvite,
        usersSortInvite,
        subscriberStatusSortInvite,
        detalizationSortInvite,
        lineDateInvite,
        selectedLinksInviteByGroup,
        toggleSelectedLinkSiteByGroup,
        setLineDateInvite,
        setStartDateInvite,
        setEndDateInvite,
        setDetalizationSortInvite,
        setUsersSortInvite,
        setSubscriberStatusSortInvite,
    } = useInviteLinksStore()
    const { handleLineDateSelect, handleDateChange } =
        useInviteLinksTimeSelectors({
            setLineDate: setLineDateInvite,
            setStartDate: setStartDateInvite,
            setEndDate: setEndDateInvite,
        })

    const [initialSelectionChecked, setInitialSelectionChecked] =
        useState(false)

    useEffect(() => {
        async function selectInitialLinks() {
            if (!activeSite) return

            const selectedLinks = selectedLinksInviteByGroup[groupId] || []

            if (selectedLinks.length > 0 || initialSelectionChecked) {
                setInitialSelectionChecked(true)
                return
            }

            const lastVisit = localStorage.getItem('lastVisit')
            const now = new Date().getTime()
            let shouldSelectLinks = false

            if (lastVisit) {
                const lastVisitDate = new Date(lastVisit).getTime()
                const timeDifference = (now - lastVisitDate) / (1000 * 60 * 60)
                if (timeDifference > 1) {
                    shouldSelectLinks = true
                }
            } else {
                shouldSelectLinks = true
            }

            if (shouldSelectLinks) {
                if (inviteLinks && inviteLinks.length > 0) {
                    const firstThreeLinks = inviteLinks
                        .sort(
                            (a, b) =>
                                new Date(b.created_at).getTime() -
                                new Date(a.created_at).getTime()
                        )
                        .slice(0, 3)
                    firstThreeLinks.forEach((link) => {
                        toggleSelectedLinkSiteByGroup(groupId, link)
                    })
                    setInitialSelectionChecked(true)
                }
            }

            localStorage.setItem('lastVisit', new Date().toISOString())
        }

        void selectInitialLinks()
    }, [
        inviteLinks,
        activeSite,
        selectedLinksInviteByGroup,
        initialSelectionChecked,
        toggleSelectedLinkSiteByGroup,
        groupId,
    ])

    const { activeWorkspace } = useActiveWorkspace()

    return (
        <>
            <div className="invite-links-groups-widget">
                <div>
                    <Title className="title">Ссылки</Title>
                    <p className="invite-links-groups-widget__link">
                        <Link
                            to={workspacePath(
                                activeWorkspace,
                                'analytics/links'
                            )}
                            className="campaigns-link"
                        >
                            Группы
                        </Link>{' '}
                        / {groupName}
                    </p>
                </div>
                <section className="invite-links-widget">
                    <AnalyticsSelectors
                        lineDate={lineDateInvite}
                        detalization={detalizationSortInvite}
                        users={usersSortInvite}
                        subscribers={subscriberStatusSortInvite}
                        datePicker={{
                            startDate: startDateInvite,
                            endDate: endDateInvite,
                        }}
                        onLineDateSelect={handleLineDateSelect}
                        onDetalizationSelect={setDetalizationSortInvite}
                        onUsersSelect={setUsersSortInvite}
                        onSubscribersSelect={setSubscriberStatusSortInvite}
                        onDateChange={{
                            onFromDateChange: (date) =>
                                handleDateChange(date, endDateInvite),
                            onToDateChange: (date) =>
                                handleDateChange(startDateInvite, date),
                        }}
                        mode="InviteLinks"
                    />

                    {/* <InviteLinksChart /> */}
                    {!isMasterLink(groupName) && (
                        <CreateSiteInviteLink
                            activeSite={activeSite}
                            onInviteLinkUpdated={onInviteLinkUpdated}
                            onRemoveInviteLink={onRemoveInviteLink}
                        />
                    )}

                    <InviteLinksSitesTable
                        groupId={groupId}
                        activeSite={activeSite}
                        activeAccount={activeAccount}
                        siteLinks={combinedInviteLinks}
                        isTableLoading={isTableLoading}
                    />
                </section>
            </div>
        </>
    )
}
